import { useCallback } from 'react';

import type { ShareChannel, ShareConfig } from '../shared/types';

import { useActionContext } from './actionContext';
import { getShareActionConfig } from './shared/shareAction';
import type { ShareActionComponentProps } from './types';

type Options<CH extends ShareChannel = ShareChannel> = {
	trackingEventName: string;
	config: ShareConfig;
	getShortUrl: (channel: CH) => string;
	getContent: (channel: CH, type: 'title' | 'body' | 'image', context: { url: string }) => string;
	getTrackingData: (channel: CH) => Readonly<Record<string, boolean | string | number | undefined>>;
	onSuccess?: (channel: CH) => void | Promise<void>;
	onFailure?: (channel: CH) => void | Promise<void>;
};

type Result =
	| {
			type: 'function';
			action: () => Promise<boolean>;
	  }
	| {
			type: 'component';
			Component: (props: ShareActionComponentProps) => React.JSX.Element;
	  };

export function useGetShareActionConfig<CH extends ShareChannel = ShareChannel>({
	getShortUrl,
	getTrackingData,
	getContent,
	trackingEventName,
	config,
	onSuccess,
	onFailure,
}: Options<CH>): (channel: CH) => Result {
	const actionContext = useActionContext();
	return useCallback(
		(channel) => {
			const shareActionConfig = getShareActionConfig(channel);
			const url = getShortUrl(channel);
			const options = {
				url,
				trackingEventName,
				trackingData: getTrackingData(channel),
				content: {
					title: getContent(channel, 'title', { url }),
					body: getContent(channel, 'body', { url }),
					image: getContent(channel, 'image', { url }),
				},
				config,
			};
			if (shareActionConfig.type === 'function') {
				return {
					type: 'function' as const,
					action: async () => {
						const success = await shareActionConfig.action(options, actionContext);
						if (success) {
							void onSuccess?.(channel);
						} else {
							void onFailure?.(channel);
						}
						return success;
					},
				};
			}
			const { Component } = shareActionConfig;
			return {
				type: 'component' as const,
				Component: ({ onAfterAction, onBeforeAction, children }: ShareActionComponentProps) => {
					const onAfterActionDecorated = useCallback(
						(success: boolean) => {
							void onAfterAction?.(success);
							if (success) {
								void onSuccess?.(channel);
							} else {
								void onFailure?.(channel);
							}
						},
						[onAfterAction],
					);
					return (
						<Component options={options} onBeforeAction={onBeforeAction} onAfterAction={onAfterActionDecorated}>
							{children}
						</Component>
					);
				},
			};
		},
		[actionContext, getShortUrl, getTrackingData, getContent, onSuccess, onFailure, trackingEventName, config],
	);
}
